import { identityClient } from '../api/clients';
import type { IdentityComponents } from '../api/identity';

export type ShareLinkTargetDto = IdentityComponents['schemas']['ShareLinkTargetDto'];

export type FindShareLinkTargetByPhoneResponse = {
  user: ShareLinkTargetDto | null;
  existsButNotShareLinkTarget: boolean;
};

function isShareLinkTargetDto(value: unknown): value is ShareLinkTargetDto {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<ShareLinkTargetDto>;
  return typeof candidate.id === 'string' && candidate.id.trim() !== '';
}

function parseFindShareLinkTargetResponse(
  value: unknown,
): FindShareLinkTargetByPhoneResponse | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const candidate = value as {
    user?: unknown;
    existsButNotShareLinkTarget?: unknown;
  };
  if (typeof candidate.existsButNotShareLinkTarget !== 'boolean') {
    return null;
  }

  if (
    candidate.user !== undefined &&
    candidate.user !== null &&
    !isShareLinkTargetDto(candidate.user)
  ) {
    return null;
  }

  return {
    user: candidate.user == null ? null : candidate.user,
    existsButNotShareLinkTarget: candidate.existsButNotShareLinkTarget,
  };
}

function responseErrorBody(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error === undefined) {
    return '';
  }

  try {
    return JSON.stringify(error);
  } catch {
    return '';
  }
}

export class CreateShareLinkTargetError extends Error {
  readonly status: number;
  readonly responseBody: string;

  constructor(status: number, responseBody: string) {
    super(`Creating the share-link target failed (HTTP ${status}).`);
    this.name = 'CreateShareLinkTargetError';
    this.status = status;
    this.responseBody = responseBody;
  }
}

export async function getShareLinkTargets(): Promise<ShareLinkTargetDto[]> {
  const { data, response } = await identityClient().GET('/Account/ShareLinkTargets');
  if (
    !response.ok ||
    data === undefined ||
    !Array.isArray(data) ||
    !data.every(isShareLinkTargetDto)
  ) {
    throw new Error(`Loading share-link targets failed (HTTP ${response.status})`);
  }

  return data;
}

export async function findShareLinkTargetByPhone(
  phoneNumber: string,
): Promise<FindShareLinkTargetByPhoneResponse> {
  const { data, response } = await identityClient().GET('/Account/ShareLinkTargets/by-phone', {
    params: { query: { phone: phoneNumber } },
  });
  const lookup = parseFindShareLinkTargetResponse(data);
  if (!response.ok || lookup === null) {
    throw new Error(`Share-link target lookup failed (HTTP ${response.status})`);
  }

  return lookup;
}

export async function createShareLinkTarget(
  phoneNumber: string,
  displayName: string,
): Promise<ShareLinkTargetDto> {
  const result = await identityClient().POST('/Account/ShareLinkTargets', {
    body: { phoneNumber, displayName },
  });

  if (!result.response.ok) {
    throw new CreateShareLinkTargetError(result.response.status, responseErrorBody(result.error));
  }

  if (result.data !== undefined && isShareLinkTargetDto(result.data)) {
    return result.data;
  }

  throw new Error('No share-link target returned from API.');
}

export async function createIncidentShareLink(incidentId: string, userId: string): Promise<string> {
  const { data, response } = await identityClient().GET(
    '/Account/CreateIncidentShareLink/{incidentId}/for-user/{userId}',
    { params: { path: { incidentId, userId } } },
  );
  if (!response.ok || typeof data !== 'string' || data.trim() === '') {
    throw new Error(`Creating the incident share link failed (HTTP ${response.status})`);
  }

  return data;
}
